/**
 * 动态路由生成器
 * 根据后端返回的菜单/规则数据生成Vue Router路由配置
 */

import { generateRoutePath, normalizePath, buildTree } from './pathGenerator'

// 路径到组件的映射配置
// 这里定义了已知路径对应的组件，未知路径将使用通用组件
const routeComponentMap = {
  // 精确匹配的路径
  '/home': () => import('@/views/Home/Home.vue'),
  '/login': () => import('@/views/Login/Login.vue'),
  '/clientele': () => import('@/views/Clientele/Clientele.vue'),
  '/cases': () => import('@/views/Cases/Cases.vue'),
  '/ajgl': () => import('@/views/Cases/Cases.vue'), // 案管中心复用Cases组件
  '/casemanagement': () => import('@/views/CaseManagement/Managemnt.vue'),
  '/finance': () => import('@/views/Finance/Finance.vue'),
  '/personnel': () => import('@/views/Personnel/Personnel.vue'),
  '/synthesis': () => import('@/views/Synthesis/Synthesis.vue'),
  '/realty': () => import('@/views/Realty/Realty.vue'),
  '/selfowned': () => import('@/views/SelfOwned/SelfOwned.vue'),
  '/system': () => import('@/views/System/System.vue'),
  
  // 模式匹配的路径（使用对应的父组件）
  '/clientele/*': () => import('@/views/Clientele/Clientele.vue'),
  '/cases/*': () => import('@/views/Cases/Cases.vue'),
  '/casemanagement/*': () => import('@/views/CaseManagement/Managemnt.vue'),
  '/finance/*': () => import('@/views/Finance/Finance.vue'),
  '/personnel/*': () => import('@/views/Personnel/Personnel.vue'),
  '/synthesis/*': () => import('@/views/Synthesis/Synthesis.vue'),
  '/system/*': () => import('@/views/System/System.vue'),
  
  // 默认通用组件（用于未知路径）
  'default': () => import('@/views/Home/Home.vue')
}

/**
 * 根据节点类型和路径获取对应的Vue组件
 * @param {Object} node - 菜单节点对象
 * @param {string} path - 路由路径
 * @returns {Function} 返回一个返回Promise的组件函数
 */
export function getComponentForNode(node, path) {
  // 1. 精确匹配
  if (routeComponentMap[path]) {
    return routeComponentMap[path]
  }
  
  // 2. 模式匹配
  for (const [pattern, component] of Object.entries(routeComponentMap)) {
    if (pattern.includes('*')) {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$')
      if (regex.test(path)) {
        return component
      }
    }
  }
  
  // 3. 根据节点类型选择
  // menu字段：1表示菜单，2表示操作节点
  if (node.menu === 1) {
    // 菜单节点通常需要布局组件，但这里我们暂时使用通用组件
    // 实际项目中可以根据需要返回不同的布局组件
    return routeComponentMap['default']
  }
  
  // 4. 操作节点或未知类型
  return routeComponentMap['default']
}

/**
 * 生成路由名称
 * @param {Object} node - 菜单节点对象
 * @param {string} path - 路由路径
 * @returns {string} 路由名称
 */
export function generateRouteName(node, path) {
  // 优先使用节点的id和path组合，确保唯一性
  if (node.id) {
    return `route_${node.id}`
  }
  
  // 其次使用路径生成
  return path.replace(/\//g, '_').replace(/^_|_$/g, '').replace(/_{2,}/g, '_')
}

/**
 * 将单个菜单节点转换为路由配置
 * @param {Object} node - 菜单节点对象
 * @param {Object} parentNode - 父节点对象（可选）
 * @returns {Object} Vue Router路由配置对象
 */
export function convertNodeToRoute(node, parentNode = null) {
  // 生成路由路径
  const path = generateRoutePath(node, parentNode)
  
  // 生成路由名称
  const name = generateRouteName(node, path)
  
  // 构建路由配置
  const route = {
    path: normalizePath(path),
    name: name,
    meta: {
      title: node.title || node.name || '未命名页面',
      originalTitle: node.title, // 保存原始标题
      requiresAuth: true, // 默认需要认证
      isMenu: node.menu === 1, // 是否是菜单项
      icon: node.icon || '',
      apiPath: node.apisrc, // 保存API路径，供组件使用
      nodeId: node.id, // 保存节点ID，用于权限校验等
      parentId: node.pid // 保存父节点ID
    }
  }
  
  // 动态导入组件
  route.component = getComponentForNode(node, path)
  
  // 处理子节点
  if (node.children && Array.isArray(node.children) && node.children.length > 0) {
    route.children = node.children.map(child => 
      convertNodeToRoute(child, node)
    )
    
    // 如果有子路由，设置重定向到第一个子路由
    if (route.children.length > 0) {
      route.redirect = route.children[0].path
    }
  }
  
  return route
}

/**
 * 将平铺的菜单数据转换为树形结构，然后生成路由配置
 * @param {Array} menuData - 平铺的菜单数据数组
 * @returns {Array} 路由配置数组
 */
export function generateRoutesFromMenuData(menuData) {
  if (!Array.isArray(menuData) || menuData.length === 0) {
    console.warn('菜单数据为空，无法生成路由')
    return []
  }
  
  try {
    // 构建树形结构
    const menuTree = buildTree(menuData)
    
    // 将树形结构转换为路由配置
    const routes = menuTree.map(node => convertNodeToRoute(node))
    
    console.log('动态路由生成完成，共生成', routes.length, '个顶级路由')
    return routes
  } catch (error) {
    console.error('生成动态路由时出错:', error)
    return []
  }
}

/**
 * 过滤出需要生成路由的节点
 * 根据业务逻辑，可能只需要菜单节点（menu=1），或者所有节点
 * @param {Array} nodes - 原始节点数组
 * @param {Object} options - 过滤选项
 * @returns {Array} 过滤后的节点数组
 */
export function filterRouteNodes(nodes, options = {}) {
  const defaultOptions = {
    onlyMenu: true, // 是否只包含菜单节点
    minStatus: 1,   // 状态值大于等于此值的节点
    excludeTypes: [] // 排除的类型
  }
  
  const config = { ...defaultOptions, ...options }
  
  return nodes.filter(node => {
    // 状态过滤
    if (node.status !== undefined && node.status < config.minStatus) {
      return false
    }
    
    // 菜单类型过滤
    if (config.onlyMenu && node.menu !== 1) {
      return false
    }
    
    // 排除特定类型
    if (config.excludeTypes.length > 0 && node.type) {
      if (config.excludeTypes.includes(node.type)) {
        return false
      }
    }
    
    return true
  })
}

/**
 * 动态添加路由到Vue Router实例
 * @param {Object} router - Vue Router实例
 * @param {Array} dynamicRoutes - 动态路由配置数组
 * @param {Array} staticRoutes - 静态路由配置数组（可选）
 */
export function addDynamicRoutes(router, dynamicRoutes, staticRoutes = []) {
  if (!router || (!router.addRoutes && !router.addRoute)) {
    console.error('无效的Vue Router实例')
    return false
  }
  
  if (!Array.isArray(dynamicRoutes) || dynamicRoutes.length === 0) {
    console.warn('没有动态路由可添加')
    return false
  }
  
  try {
    // 先添加静态路由（如果有）
    if (Array.isArray(staticRoutes) && staticRoutes.length > 0) {
      if (router.addRoutes) {
        // Vue Router 3.x
        router.addRoutes(staticRoutes)
      } else {
        // Vue Router 4+
        staticRoutes.forEach(route => router.addRoute(route))
      }
    }
    
    // 添加动态路由
    if (router.addRoutes) {
      // Vue Router 3.x
      router.addRoutes(dynamicRoutes)
    } else {
      // Vue Router 4+
      dynamicRoutes.forEach(route => router.addRoute(route))
    }
    
    // 添加404通配路由（必须在最后）
    const notFoundRoute = {
      path: '*',
      redirect: '/404'
    }
    
    if (router.addRoutes) {
      router.addRoutes([notFoundRoute])
    } else {
      router.addRoute(notFoundRoute)
    }
    
    console.log('动态路由添加成功，共添加', dynamicRoutes.length, '个动态路由')
    return true
  } catch (error) {
    console.error('添加动态路由时出错:', error)
    return false
  }
}

/**
 * 初始化动态路由系统
 * 这是一个完整的流程：获取数据 -> 生成路由 -> 添加到路由器
 * @param {Object} router - Vue Router实例
 * @param {Function} fetchMenuData - 获取菜单数据的函数，返回Promise
 * @param {Object} options - 配置选项
 * @returns {Promise<boolean>} 是否成功初始化
 */
export async function setupDynamicRoutes(router, fetchMenuData, options = {}) {
  if (!router || !fetchMenuData || typeof fetchMenuData !== 'function') {
    console.error('参数错误：需要有效的router实例和fetchMenuData函数')
    return false
  }
  
  const defaultOptions = {
    filterOptions: {
      onlyMenu: true,
      minStatus: 1
    },
    staticRoutes: [], // 静态路由
    onSuccess: null,  // 成功回调
    onError: null     // 错误回调
  }
  
  const config = { ...defaultOptions, ...options }
  
  try {
    console.log('开始加载动态路由...')
    
    // 1. 获取菜单数据
    const response = await fetchMenuData()
    
    // 处理不同的响应格式
    let menuData = null
    
    console.log('API响应原始数据:', response)
    
    // 情况1：response本身就是数组（直接返回菜单数据）
    if (Array.isArray(response)) {
      menuData = response
    }
    // 情况2：response是Axios响应对象，且response.data是数组
    else if (response && response.data && Array.isArray(response.data)) {
      menuData = response.data
    }
    // 情况3：response是对象，有code和data字段，且data是数组
    else if (response && response.code === 200 && Array.isArray(response.data)) {
      menuData = response.data
    }
    // 情况4：response是对象，有data字段，但data不是数组，可能是分页结构
    else if (response && response.data && response.data.list && Array.isArray(response.data.list)) {
      menuData = response.data.list
    }
    // 情况5：response.data是一个对象，键可能为数字字符串（如用户提供的例子）
    else if (response && response.data && typeof response.data === 'object') {
      const dataObj = response.data
      const keys = Object.keys(dataObj)
      
      // 尝试提取所有数字键的值（如"0", "1", "99"等）
      const numericKeys = keys.filter(key => {
        const num = parseInt(key)
        return !isNaN(num) && String(num) === key && num >= 0
      })
      
      if (numericKeys.length > 0) {
        // 将数字键对象转换为数组，按键的数字顺序排序
        menuData = numericKeys
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(key => dataObj[key])
        console.log('将数字键对象转换为数组，共', menuData.length, '条记录')
      } else if (Array.isArray(dataObj.data)) {
        // 可能是嵌套的data字段
        menuData = dataObj.data
      } else if (dataObj.code === 200 && Array.isArray(dataObj.data)) {
        // 另一种嵌套格式
        menuData = dataObj.data
      } else if (Array.isArray(dataObj)) {
        // 直接是数组（虽然理论上不会到这里，因为前面检查过了）
        menuData = dataObj
      } else if (dataObj.code === 200 && Array.isArray(dataObj.list)) {
        // 分页结构
        menuData = dataObj.list
      }
    }
    // 其他未知格式
    else {
      console.error('获取菜单数据失败，响应格式不支持:', response)
      if (config.onError) config.onError(new Error('菜单数据格式错误'))
      return false
    }
    
    if (!menuData || menuData.length === 0) {
      console.warn('菜单数据为空，无法生成动态路由')
      if (config.onError) config.onError(new Error('菜单数据为空'))
      return false
    }
    
    console.log('获取到菜单数据，共', menuData.length, '条记录')
    console.log('菜单数据示例:', menuData[0])
    
    // 2. 过滤节点
    const filteredNodes = filterRouteNodes(menuData, config.filterOptions)
    
    if (filteredNodes.length === 0) {
      console.warn('过滤后没有可用的菜单节点')
    } else {
      console.log('过滤后得到', filteredNodes.length, '个菜单节点')
    }
    
    // 3. 生成路由配置
    const dynamicRoutes = generateRoutesFromMenuData(filteredNodes)
    
    // 4. 添加到路由器
    const success = addDynamicRoutes(router, dynamicRoutes, config.staticRoutes)
    
    if (success && config.onSuccess) {
      config.onSuccess(dynamicRoutes)
    }
    
    return success
  } catch (error) {
    console.error('初始化动态路由时出错:', error)
    if (config.onError) config.onError(error)
    return false
  }
}

export default {
  generateRoutesFromMenuData,
  convertNodeToRoute,
  filterRouteNodes,
  addDynamicRoutes,
  setupDynamicRoutes,
  getComponentForNode,
  generateRouteName
}
